// helper: sort by label (case-insensitive, handles apostrophes nicely)
const sortByLabel = (arr) =>
  [...arr].sort((a, b) =>
    String(a.label || "").localeCompare(String(b.label || ""), undefined, {
      sensitivity: "base",
    })
  );

export const other = [
  { value: "i", label: "Not Affiliated / Not in a school" },
  { value: "homeschool", label: "Other Homeschool (not listed)" },
  { value: "other", label: "Other School (not listed)" },
];

export const newProvidenceSchools = [
  // Public (Government) secondary schools — New Providence
  { value: "af_adderley_jh", label: "A. F. Adderley Junior High School" },
  { value: "anatol_rodgers_hs", label: "Anatol Rodgers High School" },
  { value: "ch_reeves_jh", label: "C. H. Reeves Junior High School" },
  { value: "cc_sweeting_sh", label: "C. C. Sweeting Senior High School" },
  { value: "ci_gibson_sh", label: "C. I. Gibson Senior High School" },
  { value: "cr_walker_sh", label: "C. R. Walker Senior High School" },
  { value: "cv_bethel_sh", label: "C. V. Bethel Senior High School" },
  { value: "dw_davis_jh", label: "D. W. Davis Junior High School" },
  { value: "doris_johnson_sh", label: "Doris Johnson Senior High School" },
  { value: "government_high", label: "Government High School" },
  { value: "ho_nash_jh", label: "H. O. Nash Junior High School" },
  { value: "lw_young_jh", label: "L. W. Young Junior High School" },
  { value: "rm_bailey_sh", label: "R. M. Bailey Senior High School" },
  { value: "sc_mcpherson_jh", label: "S. C. McPherson Junior High School" },
  { value: "ta_thompson_jh", label: "T. A. Thompson Junior High School" },

  // Private / Independent schools — New Providence
  { value: "aquinas", label: "Aquinas College" },
  { value: "bahamas_academy", label: "Bahamas Academy" },
  { value: "charles_w_saunders", label: "Charles W. Saunders Baptist School" },
  { value: "fds_success", label: "FDS Success Learning Secondary School" },
  { value: "genesis", label: "Genesis Academy" },
  { value: "ka", label: "Kingsway Academy" },
  { value: "kc", label: "King's College School" },
  { value: "lyford_cay", label: "Lyford Cay International School" },
  { value: "mt_carmel_prep", label: "Mt. Carmel Preparatory Academy" },
  { value: "nassau_christian", label: "Nassau Christian Academy" },
  { value: "qc", label: "Queen's College" },
  { value: "sac", label: "St. Augustine's College" },
  { value: "sta", label: "St. Andrew's School" },
  { value: "st_annes", label: "St. Anne's School" },
  { value: "st_johns", label: "St. John's College" },
  { value: "south_haven", label: "South Haven Christian Academy" },
  { value: "sunland_baptist", label: "Sunland Baptist Academy" },
  { value: "tabernacle_baptist", label: "Tabernacle Baptist Christian Academy" },
  { value: "tambearly", label: "Tambearly International School" },
  { value: "temple_christian", label: "Temple Christian School" },
  { value: "teleos_christian", label: "Teleos Christian School" },
  { value: "windsor", label: "Windsor School" },

  // Homeschools — New Providence
{ value: "kevelyn_wright_home_school", label: "Kevelyn Wright Homeschool" },
{ value: "providence_home_school_home_school", label: "Providence Homeschool" },
];

export const grandBahamaSchools = [
  { value: "st_georges_gb", label: "St. George's High School" },
];

export const androsSchools = [
  { value: "central_andros_high", label: "Central Andros High School" },
];

export const exumaSchools = [
  { value: "ln_coakley_high", label: "L.N. Coakley High School" },
];

export const biminiSchools = [
  { value: "louise_mcdonald_high", label: "Louise McDonald High School" },
];

export const inaguaSchools = [
  { value: "inagua_all_age", label: "Inagua All Age School" },
];

// Unknown island — cannot confirm placement
export const unknownSchools = [
  { value: "bt_miller", label: "B.T. Miller" },
];

// grouped + sorted (each category alphabetized by label)
export const groupedSchools = [
  { label: "New Providence", options: sortByLabel(newProvidenceSchools) },
  { label: "Grand Bahama", options: sortByLabel(grandBahamaSchools) },
  { label: "Andros", options: sortByLabel(androsSchools) },
  { label: "Exuma", options: sortByLabel(exumaSchools) },
  { label: "Bimini", options: sortByLabel(biminiSchools) },
  { label: "Inagua", options: sortByLabel(inaguaSchools) },
  { label: "-", options: sortByLabel(unknownSchools) },
  { label: "Other", options: sortByLabel(other) },
];

export const flattenSchoolOptions = () =>
  groupedSchools.flatMap((g) => g.options);

// Two options in the "Other" group are not schools — they are a prompt to type one.
export const HOMESCHOOL_VALUE = "homeschool";
export const OTHER_SCHOOL_VALUE = "other";

export const isCustomSchoolValue = (value) =>
  value === HOMESCHOOL_VALUE || value === OTHER_SCHOOL_VALUE;

// A typed-in school is stored as the name with its kind appended, so a homeschool can
// still be told apart from an unlisted school after the fact. Registration has always
// written this format; encoding and decoding live here so the two screens cannot drift.
const SUFFIX = { [HOMESCHOOL_VALUE]: "_home_school", [OTHER_SCHOOL_VALUE]: "_other" };

export const encodeCustomSchool = (name, optionValue) => {
  const trimmed = String(name ?? "").trim();
  if (!trimmed) return optionValue;

  return `${trimmed}${SUFFIX[optionValue] ?? SUFFIX[OTHER_SCHOOL_VALUE]}`;
};

/**
 * Split a stored school back into the dropdown choice and the typed name.
 *
 * Anything the dropdown does not recognise is treated as an unlisted school rather than
 * shown as an empty select — a student who typed their school should see it again.
 */
export const decodeSchool = (stored) => {
  const raw = String(stored ?? "").trim();
  if (!raw) return { optionValue: "", customName: "" };

  for (const [optionValue, suffix] of Object.entries(SUFFIX)) {
    if (raw.length > suffix.length && raw.endsWith(suffix)) {
      return { optionValue, customName: raw.slice(0, -suffix.length) };
    }
  }

  const option = findSchoolOption(raw);
  if (option) return { optionValue: option.value, customName: "" };

  return { optionValue: OTHER_SCHOOL_VALUE, customName: raw };
};

export const findSchoolOption = (raw) => {
  if (!raw) return null;
  const needle = String(raw).trim().toLowerCase();

  const options = flattenSchoolOptions();
  return (
    options.find((o) => String(o.value).trim().toLowerCase() === needle) ||
    options.find((o) => String(o.label).trim().toLowerCase() === needle) ||
    null
  );
};